import React, { FC, useState, ChangeEvent } from 'react';
import { useForm } from 'react-hook-form';

import Visibility from '@mui/icons-material/VisibilityOutlined';
import VisibilityOff from '@mui/icons-material/VisibilityOffOutlined';
import {
  Box,
  Typography,
  InputAdornment,
  IconButton,
  Link,
} from '@mui/material';
import InputField from 'shared/components/input/input.component';

import useLogin from '../Login.service';
import MaskedPhoneNumber from 'components/MaskedPhoneNumber/MaskedPhoneNumber';
import LoadingButton from 'components/loading-button/loading-button.component';
import { STRINGS } from 'utils/constants';

interface Inputs {
  username: string;
  password: string;
}

const LoginForm: FC = () => {
  const { login, loginError, loading } = useLogin();
  const [showPassword, setShowPassword] = useState(false);
  const [rememberPassword, setRememberPassword] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState<string>('');

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<Inputs>();

  const handleOnChange = ({ target }: ChangeEvent<HTMLInputElement>) =>
    setPhoneNumber(target.value);

  const handleOnBlur = () => setValue('username', phoneNumber);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box>
      <form
        onSubmit={handleSubmit(login)}
        autoComplete={rememberPassword ? 'on' : 'off'}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <InputField
            label='Sorenson Phone Number'
            placeholder='Sorenson Phone Number'
            error={!!errors.username || loginError}
            InputLabelProps={{ shrink: true, variant: 'standard' }}
            InputProps={{
              inputComponent: MaskedPhoneNumber as any,
            }}
            helperText={errors?.username?.message}
            {...register('username', {
              required: 'Sorenson Phone Number is required!',
              onChange: handleOnChange,
              onBlur: handleOnBlur,
            })}
          />

          <InputField
            label='Sorenson Password'
            placeholder='Sorenson Password'
            error={!!errors.password || loginError}
            helperText={errors?.password?.message}
            type={showPassword ? 'text' : 'password'}
            autoComplete={rememberPassword ? 'on' : 'new-password'}
            InputLabelProps={{ shrink: true, variant: 'standard' }}
            sx={{ marginBottom: '24px' }}
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <IconButton
                    aria-label='toggle password visibility'
                    onClick={handleClickShowPassword}
                    edge='end'
                  >
                    {showPassword ? (
                      <Visibility sx={{ color: 'text.disabled' }} />
                    ) : (
                      <VisibilityOff sx={{ color: 'text.disabled' }} />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            {...register('password', {
              required: 'Sorenson Password is required!',
            })}
          />
          {loginError && (
            <Typography sx={{ color: 'error.main', fontSize: '14px' }}>
              {STRINGS.ERROR_STRINGS.SIGN_IN_FAILURE}
            </Typography>
          )}
          <Box sx={{ margin: '16px 0' }}>
            <Link
              href='https://www.sorensonvrs.com/secured_pword_reset'
              underline='none'
              sx={{
                color: 'text.primary',
                fontSize: '14px',
                textAlign: 'center',
                fontWeight: '700',
              }}
            >
              Forgot Password?
            </Link>
          </Box>
          <LoadingButton type='submit' loading={loading}>
            <Typography sx={{ fontSize: '14px', fontWeight: '700' }}>
              Continue
            </Typography>
          </LoadingButton>
        </Box>
      </form>
    </Box>
  );
};

export default LoginForm;
